import React from 'react';
import { useQuery } from '@apollo/client';
import { loader } from 'graphql.macro';
import {
	BaseMovablesLocationFragment,
	GetAdminLocations,
	GetAdminLocationsVariables,
	GetMovablesLocations,
	GetMovablesLocationsVariables,
	GetMovablesLocations_movablesLocations,
} from '../../../GraphQL';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckSquare, faEdit, faPlus, faSquare, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { tokenizeStringWithQuotesBySpaces } from '../../../helper';
import { useTranslation } from 'react-i18next';
import Button from '@ssg/common/Components/Button';
import Loading from '@ssg/common/Components/Loading';
import Table from '@ssg/common/Components/Table';
import MovablesModal from './MovablesLocationModal';
import MovablesFilters from './MovablesFilters';
import useDebouncedState from '@ssg/common/Hooks/useDebouncedState';
import Fuse from 'fuse.js';
import useArrayToggler from '@ssg/common/Hooks/useArrayToggler';
import MovablesPrintLabel from './MovablesPrintLabel';
import { useDeleteMovablesLocationsMutation } from '@ssg/common/GraphQL/indexV2';
import Modal, { ModalSize } from '@ssg/common/Components/Modal';

const GET_MOVABLES = loader('src/GraphQL/MovablesLocations/GetMovablesLocations.gql');
const GET_LOCATIONS = loader('src/GraphQL/Locations/GetAdminLocations.gql');
const PRINT_LIMIT = 300;

const Movables: React.FC = (): React.ReactElement => {
	const { t } = useTranslation();

	const [searchTerm, setSearchTerm] = useDebouncedState('', 100);
	const [location, setLocation] = React.useState<string | undefined>(undefined);

	const [createMovables, setCreateMovables] = React.useState(false);
	const [updateMovables, setUpdateMovables] = React.useState(false);
	const [deleteMovable, setDeleteMovable] = React.useState(false);
	const [deleteMovables, setDeleteMovables] = React.useState(false);
	const [MovablesData, setMovablesData] = React.useState<GetMovablesLocations_movablesLocations>();
	const [activeTab, setActiveTab] = React.useState<'view' | 'print' | 'delete'>('view');
	const [selectedRows, toggleSelectedRows, toggleAllRows, clearSelectedRows] = useArrayToggler<string>([]);

	const [selectedRowsBatches, setSelectedRowsBatches] = React.useState<string[][]>([]);

	React.useEffect(() => {
		if (selectedRows.length > PRINT_LIMIT) {
			const slicedArray: string[][] = [];
			for (let i = 0; i < selectedRows.length; i += PRINT_LIMIT) {
				const chunk = selectedRows.slice(i, i + PRINT_LIMIT);
				slicedArray.push(chunk);
			}
			setSelectedRowsBatches(slicedArray);
		} else {
			setSelectedRowsBatches([selectedRows]);
		}
	}, [selectedRows]);

	React.useEffect(() => console.log(selectedRowsBatches));

	const { data: locations } = useQuery<GetAdminLocations, GetAdminLocationsVariables>(GET_LOCATIONS, {
		variables: {
			movableLocationsOnly: true,
		},
		onCompleted(data) {
			setLocation(data.locationsAdmin.slice().sort((a, b) => a.name.localeCompare(b.name))[0].id);
		},
	});
	const {
		data: movables,
		loading,
		refetch,
	} = useQuery<GetMovablesLocations, GetMovablesLocationsVariables>(GET_MOVABLES, {
		variables: {
			location: location,
		},
		skip: typeof location === 'undefined',
	});

	const movablesMap = new Map<string, BaseMovablesLocationFragment>();

	movables?.movablesLocations.forEach(m => {
		locations?.locationsAdmin.forEach(l => {
			if (m.location.id === l.id) {
				movablesMap.set(m.id, {
					id: m.id,
					name: m.name,
					volume: m.volume,
					accVolume: m.accVolume,
					location: { id: l.id, name: l.name, address: l.address },
				});
			}
		});
	});

	const movablesArray = Array.from(movablesMap.values());

	const movablesLocations = React.useMemo(() => movablesArray ?? [], [movablesArray]);

	const fuse = React.useMemo(
		() =>
			new Fuse(movablesLocations, {
				shouldSort: true,
				threshold: 0.1,
				keys: ['name'],
			}),
		[movablesLocations],
	);

	const filteredMovables =
		searchTerm.length > 0
			? fuse
					.search({
						$and: tokenizeStringWithQuotesBySpaces(searchTerm).map((searchToken: string) => {
							const orFields: Fuse.Expression[] = [{ name: searchToken }];

							return {
								$or: orFields,
							};
						}),
					})
					.sort((a, b) => (a.score ?? Number.MAX_SAFE_INTEGER) - (b.score ?? Number.MAX_SAFE_INTEGER))
					.map(v => v.item)
			: movablesLocations;

	const isAllSelected = React.useMemo((): boolean => {
		const filteredMovablesLength = filteredMovables.length;
		const filteredMovablesIds = filteredMovables.map(m => m.id);
		const filteredMovablesInSelectedRowsLength = selectedRows.filter(r => filteredMovablesIds.includes(r)).length;

		return filteredMovablesLength === filteredMovablesInSelectedRowsLength;
	}, [filteredMovables, selectedRows]);

	const [deleteMovableLocations, { loading: deleteLoading }] = useDeleteMovablesLocationsMutation();

	const handleDelete = async (ids: string[]): Promise<void> => {
		await deleteMovableLocations({
			variables: {
				ids,
			},
			onCompleted(data) {
				if (data.deleteMovablesLocations) {
					refetch();
					clearSelectedRows();
					setDeleteMovables(false);
				}
			},
		});
	};

	return (
		<div>
			<header className="flex flex-row items-end justify-between">
				<MovablesFilters setSearchTerm={setSearchTerm} setLocation={setLocation} activeTab={activeTab} setActiveTab={setActiveTab} locations={locations?.locationsAdmin ?? []} />

				<Button primary text="movablesLocations.create" icon={faPlus} onClick={() => setCreateMovables(true)} />
			</header>
			<div className="text-blue relative bg-white pb-1">
				<div className="mt-2 flex flex-row space-x-2">
					{activeTab === 'print' &&
						selectedRowsBatches.map((batch, i) => (
							<MovablesPrintLabel
								key={'batch' + i}
								movables={movablesLocations.filter(m => batch.includes(m.id)).sort((a, b) => a.name.localeCompare(b.name))}
								buttonText={
									selectedRowsBatches.length > 1
										? `${t('movablesLocations.printSelected')} (${i * PRINT_LIMIT + 1} - ${
												batch.length % PRINT_LIMIT !== 0 ? batch.length + PRINT_LIMIT * i : PRINT_LIMIT + i * PRINT_LIMIT
										  })`
										: `${t('movablesLocations.printSelected')} (${batch.length})`
								}
								disabled={selectedRows.length === 0}
							/>
						))}
					{activeTab === 'delete' && (
						<Button
							danger
							text={t('movablesLocations.deleteMany', { count: selectedRows.length })}
							icon={faTrashAlt}
							onClick={() => setDeleteMovables(true)}
							disabled={selectedRows.length === 0}
						/>
					)}
				</div>
				{loading ? (
					<div className="h-40">
						<Loading />
					</div>
				) : (
					<Table
						data={filteredMovables ?? []}
						onRowClick={activeTab !== 'view' ? m => toggleSelectedRows(m.id) : undefined}
						keySelector={m => m.id}
						columns={[
							activeTab === 'view'
								? {
										label: 'movablesLocations.print',
										selectFn: m => <MovablesPrintLabel movables={[m]} />,
								  }
								: {
										label: '',
										selectFn: m => (
											<div className="">{selectedRows.includes(m.id) ? <FontAwesomeIcon icon={faCheckSquare} size="lg" /> : <FontAwesomeIcon icon={faSquare} size="lg" />}</div>
										),

										icon: isAllSelected ? faCheckSquare : faSquare,

										actionFn: () => toggleAllRows([...filteredMovables.map(m => m.id)]),
								  },
							{
								label: 'common.name',
								selectFn: m => <p className="font-medium">{m.name}</p>,
								sortFn: (a, b) => (a.name ?? '').localeCompare(b.name ?? ''),
								beginAsSorted: true,
							},
							{
								label: 'movablesLocations.volume',
								numeric: true,
								selectFn: m => <p className="font-medium">{m.volume} m&sup3;</p>,
								sortFn: (a, b) => a.volume - b.volume,
							},
							{
								label: 'movablesLocations.parentLocation',
								selectFn: m => (
									<p>
										{m.location.name}
										<br />
										{m.location.address.postalCode} {m.location.address.city}
									</p>
								),
							},
							{
								label: 'common.edit',
								classNameTh: 'text-right',
								selectFn: m => (
									<div className="flex content-start justify-end text-right">
										<FontAwesomeIcon
											icon={faEdit}
											size="lg"
											onClick={() => {
												setMovablesData(m);
												setUpdateMovables(true);
											}}
											className="cursor-pointer"
										/>
									</div>
								),
							},
							{
								label: 'common.delete',
								classNameTh: 'text-right',
								selectFn: m => (
									<div className="text-red flex content-start justify-end text-right">
										<FontAwesomeIcon
											icon={faTrashAlt}
											size="lg"
											onClick={() => {
												setMovablesData(m);
												setDeleteMovable(true);
											}}
											className="cursor-pointer"
										/>
									</div>
								),
							},
						]}
					/>
				)}
			</div>

			{createMovables && (
				<MovablesModal
					open={createMovables}
					close={() => setCreateMovables(false)}
					submitCb={() => {
						refetch();
						setCreateMovables(false);
					}}
					locationsData={locations?.locationsAdmin ?? []}
				/>
			)}

			{updateMovables && (
				<MovablesModal
					open={updateMovables}
					close={() => setUpdateMovables(false)}
					edit
					data={MovablesData}
					submitCb={() => {
						refetch();
						setUpdateMovables(false);
					}}
					locationsData={locations?.locationsAdmin ?? []}
				/>
			)}

			{deleteMovable && (
				<MovablesModal
					erase
					open={deleteMovable}
					close={() => setDeleteMovable(false)}
					data={MovablesData}
					submitCb={() => {
						refetch();
						setDeleteMovable(false);
					}}
					locationsData={locations?.locationsAdmin ?? []}
				/>
			)}
			{deleteMovables && (
				<Modal
					title={t('movablesLocations.deleteMany', { count: selectedRows.length })}
					size={ModalSize.SMALL}
					visible={deleteMovables}
					close={() => setDeleteMovables(false)}
					body={
						<>
							<p>{t('movablesLocations.deleteMany', { count: selectedRows.length })}?</p>
							<div className="flex space-x-2">
								<Button danger disabled={selectedRows.length === 0} loading={deleteLoading} text="common.delete" className="mt-3" onClick={() => handleDelete(selectedRows)} />
								<Button secondary text="common.cancel" className="mt-3" onClick={() => setDeleteMovables(false)} />
							</div>
						</>
					}
				/>
			)}
		</div>
	);
};

export default Movables;

